import { Injectable } from '@angular/core';
import {
  Component as BrComponent,
  isContainerItem,
  Page,
} from '@bloomreach/spa-sdk';
import distributionHistoricalData from '@graphql/pricing-distribution/distribution-history-international.graphql';
import premiumDiscountDataQuery from '@graphql/pricing-distribution/premium-discount.graphql';
import productDetailsNjTreasuryQuery from '@graphql/product-detail/product-details-nj-treasury.graphql';
import productDetailsPricingQuery from '@graphql/product-detail/product-details-pricing.graphql';
import productDetailsQuery from '@graphql/product-detail/product-details.graphql';
import riskReturnProfileQuery from '@graphql/fund-performance/risk-return-profile.graphql';
import { Product } from '@models';
import { DistributionHistory } from '@products/models/distribution-history';
import { PortfolioComponentConfig } from '@products/portfolio/portfolio-component.config';
import { PortfolioService } from '@products/portfolio/services/portfolio.service';
import { DistributionHistoryService } from '@products/services/distribution-history.service';
import { FundOverviewService } from '@products/services/fund-overview.service';
import { GraphQLFundDataService } from '@products/services/graphql-fund-data.service';
import { PremiumDiscountService } from '@products/services/premium-discount.service';
import { PricingHistoryService } from '@products/services/pricing-history.service';
import { RiskReturnProfileDataService } from '@products/services/risk-return-profile-data.service';
import { EMDASH } from '@products/utils/constants/product.constants';
import { hasValues } from '@products/utils/mappers/mapper.utils';
import { SiteConfigService } from '@services';
import {
  AssetClass,
  FundId,
  PortfolioComponentId,
  ProductDetailConfiguration,
  ShareClassCode,
} from '@types';
import { Logger } from '@utils/logger';
import { removeLeadingZero } from '@utils/text/string-utils';
import cloneDeep from 'lodash/cloneDeep';
import { BehaviorSubject, forkJoin, Observable, of, ReplaySubject } from 'rxjs';
import { map, take } from 'rxjs/operators';
import {
  NjTreasuryProduct,
  NjTreasuryProductRaw,
} from '../../interactive-content/njbest/treasury-managed-price-and-performance/treasury-managed-price-and-performanc.interface';

const logger = Logger.getLogger('ProductDetailService');

export interface ProductDetail {
  fundName: string;
  investmentGroup: string;
  investmentManager: string;
  shareClassName: string;
  identifier: string;
  hasDistributions: boolean;
  url: string;
  productType: string;
  fundCategory: string;
  investmentVehicle: string;
  fundTicker: string;
  shareClassCurrency?: string;
  assetClass?: AssetClass;
  nav?: {
    highestNavValueDateIn52Weeks: string;
    highestNavValueIn52Weeks: string;
    lowestNavValueDateIn52Weeks: string;
    lowestNavValueIn52Weeks: string;
  };
  charges?: {
    annualCharge: string;
  };
  hasTax?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class ProductDetailService {
  isVIPNonLoggedIn$ = new BehaviorSubject(false);

  constructor(
    private siteConfig: SiteConfigService,
    private portfolioService: PortfolioService,
    private fundOverviewService: FundOverviewService,
    private dataService: GraphQLFundDataService,
    private pricingHistoryService: PricingHistoryService,
    private premiumDiscountService: PremiumDiscountService,
    private riskReturnProfileDataService: RiskReturnProfileDataService,
    private distributionHistoryService: DistributionHistoryService
  ) {}

  /**
   * Need to send off requests for each possible portfolio component and see which ones have data.
   * Then update list of ones with data whilst maintaining the original order.
   */
  getPortfolioComponents$(
    fundId: FundId,
    portfolioComponents: BrComponent[]
  ): Observable<BrComponent[]> {
    const componentsToShow: PortfolioComponentId[] = [];
    const subject: BehaviorSubject<BrComponent[]> = new BehaviorSubject<
      BrComponent[]
    >([]);
    subject.next([]);
    const portfolioConfigs: PortfolioComponentConfig[] = portfolioComponents
      .map((component) => this.getPortfolioComponentConfig(component))
      .filter((config) => config && config.gqlQuery);
    portfolioConfigs.forEach((config) => {
      this.portfolioService
        .getPortfolioData$(config, fundId)
        .pipe(take(1))
        .subscribe((data) => {
          if (data.success) {
            // this portfolio component has data for this fund.
            componentsToShow.push(config.componentId);
            const updatedComponentList: BrComponent[] = portfolioComponents.filter(
              (component) =>
                componentsToShow.includes(
                  this.getPortfolioComponentConfig(component)?.componentId
                )
            );
            subject.next(updatedComponentList);
          }
        });
    });
    return subject.asObservable();
  }

  /**
   * Check if, any portfolio component has valid data.
   * Return the boolean or error response accordingly.
   * Also, Intentionally typed returnType as 'any', as it can be 'boolean' for success or 'any' for error.
   * @param fundId current page fund id
   * @param configuredPortfolioComponents portfolio components configured in br response
   */
  getPortfolioComponentStatus(
    fundId: FundId,
    configuredPortfolioComponents: BrComponent[]
  ): Promise<boolean> {
    return new Promise((resolve, reject) => {
      const portfolioConfigs: PortfolioComponentConfig[] = configuredPortfolioComponents
        .map((component) => this.getPortfolioComponentConfig(component))
        .filter((config) => config && config.gqlQuery);
      let count = portfolioConfigs.length;
      portfolioConfigs.forEach((config, index) => {
        this.portfolioService
          .getPortfolioData$(config, fundId)
          .pipe(take(1))
          .subscribe(
            (data) => {
              count--;
              if (data.success) {
                // this portfolio component has data for this fund.
                count++; // so can never reach zero
                resolve(true);
              } else if (count === 0) {
                // all false
                resolve(false);
              }
            },
            (error) => reject(error)
          );
      });
    });
  }

  handlePortfolioTabSelection() {
    this.portfolioService.handlePortfolioTabSelection();
  }

  private getPortfolioComponentConfig(
    component: BrComponent
  ): PortfolioComponentConfig {
    const config: ProductDetailConfiguration = component.getModels()?.config;
    return PortfolioComponentConfig.getConfig(
      config?.componentType as PortfolioComponentId
    );
  }

  hasMarketingContent(
    page: Page,
    components: BrComponent[],
    productType: string
  ): boolean {
    if (components && components.length > 0) {
      const fundContentComponents = components.filter(
        (component) =>
          isContainerItem(component) && component.getLabel() === 'Fund Content'
      );
      if (components.length > fundContentComponents.length) {
        return true; // non fund content component so have to render
      }
      return (
        // check if any fund content component has any documents
        fundContentComponents.filter((component) => {
          const docs = component.getModels().documents;
          const location = component?.getModels()?.config.location;
          return (
            Array.isArray(docs) &&
            docs.length > 0 &&
            this.siteConfig.getFundContentCategories(productType, location)
          );
        }).length > 0
      );
    }
    return false;
  }

  getSecondLastYear(): number {
    return new Date().getFullYear() - 2;
  }

  getProductDetails$(
    fundId: FundId,
    shareClassCode: ShareClassCode
  ): Observable<ProductDetail> {
    // second last year tax data is checked as last year data is not
    // generated for fist month of current year i.e. January only
    // revamped code to get details based on Distribution History
    const year = this.getSecondLastYear();

    return forkJoin([
      this.distributionHistoryService.register(distributionHistoricalData, {
        fundId,
        shareClassCode,
      }),
      this.fundOverviewService.register(productDetailsQuery, {
        fundId,
        shareClassCode,
        year,
      }),
    ]).pipe(
      map(([perfHistoryData, product]: [DistributionHistory[], Product]) => {
        const currentShareClass = product?.shareClasses[0];
        const identifierKey = product?.productTaxonomy
          ? this.siteConfig.getDefaultIdentifierKey(product?.productTaxonomy)
          : null;
        let identifier;

        // WDE-5664: Display 'ticker' in fund title for "CA-ETF" just like non-SINGLCLASS shareclasses.
        //           Intentionally hardcoded [Ref. BA] as a workaround, as there is taxonomy mismatch between BR and PDS.
        if (
          currentShareClass &&
          currentShareClass.isEtfClass &&
          this.siteConfig.getPdsCountry() === 'CA'
        ) {
          identifier = currentShareClass.identifiers.ticker;
        } else {
          identifier =
            currentShareClass?.identifiers &&
            identifierKey in currentShareClass.identifiers
              ? currentShareClass.identifiers[identifierKey]
              : null;
        }

        const shareClassName = currentShareClass?.isSingleShareClass
          ? null
          : currentShareClass?.shareClassName;
        return {
          fundName: product?.fundName,
          investmentManager: product?.investmentManager,
          sfdrcategory: product?.sfdrCategory,
          shareClassName,
          shareClassCurrency: currentShareClass?.shareClassCurrency,
          identifier,
          hasDistributions: !!perfHistoryData?.length,
          hasTax: !!currentShareClass?.tax?.distributions?.length,
          url: currentShareClass?.link,
          productType: product?.productType,
          fundCategory: product?.fundCategory,
          investmentVehicle: product?.investmentVehicle,
          investmentGroup: product?.investmentGroup,
          fundTicker: currentShareClass?.identifiers?.ticker,
          assetClass: product?.assetClass,
          nav: {
            highestNavValueDateIn52Weeks:
              currentShareClass?.nav?.highestNavValueDateIn52Weeks,
            highestNavValueIn52Weeks:
              currentShareClass?.nav?.highestNavValueIn52Weeks,
            lowestNavValueDateIn52Weeks:
              currentShareClass?.nav?.lowestNavValueDateIn52Weeks,
            lowestNavValueIn52Weeks:
              currentShareClass?.nav?.lowestNavValueIn52Weeks,
          },
          charges: {
            annualCharge: currentShareClass?.charges?.annualCharge,
          },
        };
      })
    );
  }
  getNjTreasuryProductDetails$(
    fundId: FundId,
    shareClassCode: ShareClassCode,
    onString: string
  ): Observable<NjTreasuryProduct> {
    return this.fundOverviewService
      .registerWithoutMapper(productDetailsNjTreasuryQuery, {
        fundId,
        shareClassCode,
      })
      .pipe(
        map((data: NjTreasuryProductRaw) => {
          const currentShareClass = data?.data?.Overview?.shareclass[0];
          const currentPerfomance =
            currentShareClass?.performance?.month_end[0];
          return {
            fundName: data?.data?.Overview?.fundname,
            portfolioNum: removeLeadingZero(
              currentShareClass?.identifiers?.taid
            ),
            totalAnnualOperatingExpenses: this.addPercent(
              currentShareClass?.charges?.annchrg
                ? currentShareClass?.charges?.annchrg
                : EMDASH
            ),
            avgAnnual1YearReturn: `${this.addPercent(
              currentPerfomance?.avgannret1yr
            )}`,
            avgAnnual5YearReturn: `${this.addPercent(
              currentPerfomance?.avgannret5yr
            )}`,
            avgAnnual10YearReturn: `${this.addPercent(
              currentPerfomance?.avgannret10yr
            )}`,
            avgAnnualSinceInceptionReturn: `${this.addPercent(
              currentPerfomance?.avgannretactinc
            )}`,
            avgAnnualYearToDateReturn: `${this.addPercent(
              currentPerfomance?.avgannretytd
            )}`,
            navAsOfDate: currentShareClass?.nav?.navdate,
            navValue: currentShareClass?.nav?.navvalue,
            highestNavIn52Weeks: `${currentShareClass?.nav?.navhigh52week} ${onString} ${currentShareClass?.nav?.navhigh52weekdate}`,
            lowestNavIn52Weeks: `${currentShareClass?.nav?.navlow52week} ${onString} ${currentShareClass?.nav?.navlow52weekdate}`,
            perfAsOf: `${currentPerfomance?.asofdate}`,
          };
        })
      );
  }

  private addPercent(input: string): string {
    if (input && input !== EMDASH) {
      return `${input}%`;
    } else {
      return input;
    }
  }

  handlePricingTabSelection() {
    this.pricingHistoryService.handlePricingTabSelection();
  }

  getPricingComponents$(
    fundId: FundId,
    shareClassCode: ShareClassCode,
    pricingComponents: BrComponent[]
  ): Observable<BrComponent[]> {
    const subject: ReplaySubject<BrComponent[]> = new ReplaySubject<
      BrComponent[]
    >();
    const priceComponentIndex = pricingComponents.findIndex(
      (component) => component.getModels()?.config?.componentType === 'Price'
    );
    const premiumDiscountComponentIndex = pricingComponents.findIndex(
      (component) =>
        component.getModels()?.config?.componentType ===
        'Premium Discount Analysis'
    );
    const viivComponentIndex = pricingComponents.findIndex(
      (component) =>
        component.getModels()?.config?.componentType ===
        'Premium/Discount NAV to VIIV'
    );

    // Prepare pricing components list to show.
    // But, show pricing one by default, as it would always be there for now.
    const componentsToShow: BrComponent[] = [
      pricingComponents[priceComponentIndex],
    ];

    if (premiumDiscountComponentIndex > -1) {
      this.isPremiumDiscountDataAvailable$(fundId, shareClassCode)
        .pipe(take(1))
        .subscribe(
          (success) => {
            if (success) {
              componentsToShow.push(
                pricingComponents[premiumDiscountComponentIndex]
              );
            }
            subject.next(componentsToShow);
          },
          (error) => {
            logger.error(
              `Error in getting premium discount data in product detail for fund id ${fundId}
                and share class ${shareClassCode} `,
              error
            );
            subject.next(componentsToShow);
          }
        );
    }

    if (viivComponentIndex > -1) {
      this.isViivDataAvailable$(fundId, shareClassCode)
        .pipe(take(1))
        .subscribe(
          (success) => {
            if (success) {
              componentsToShow.push(pricingComponents[viivComponentIndex]);
            }
            subject.next(componentsToShow);
          },
          (error) => {
            logger.error(
              `Error in getting Viiv data in product detail for fund id ${fundId}
                and share class ${shareClassCode} `,
              error
            );
            subject.next(componentsToShow);
          }
        );
    } else {
      subject.next(pricingComponents);
    }

    return subject.asObservable();
  }

  getPerformanceComponents$(
    fundId: FundId,
    performanceComponents: BrComponent[]
  ): Observable<BrComponent[]> {
    const subject$: ReplaySubject<BrComponent[]> = new ReplaySubject<
      BrComponent[]
    >();
    const riskReturnProfileIndex = performanceComponents.findIndex(
      (component) =>
        component.getModels()?.config?.componentType === 'Risk Return Profile'
    );
    const componentsToShow: BrComponent[] = cloneDeep(performanceComponents);
    componentsToShow.splice(riskReturnProfileIndex, 1);

    if (riskReturnProfileIndex > -1) {
      this.isRiskReturnProfileDataAvailable$(fundId)
        .pipe(take(1))
        .subscribe(
          (success) => {
            if (success) {
              componentsToShow.splice(
                riskReturnProfileIndex,
                0,
                performanceComponents[riskReturnProfileIndex]
              );
            }
            subject$.next(componentsToShow);
          },
          (error) => {
            logger.error(
              `Error in getting risk return profile data in product detail for fund id ${fundId}`,
              error
            );
            subject$.next(componentsToShow);
          }
        );
    } else {
      subject$.next(performanceComponents);
    }
    return subject$.asObservable();
  }

  private isPremiumDiscountDataAvailable$(
    fundId: FundId,
    shareClassCode: ShareClassCode
  ): Observable<boolean> {
    return this.siteConfig.isTraded(fundId)
      ? this.premiumDiscountService
          .register(premiumDiscountDataQuery, {
            fundId,
            shareClassCode,
          })
          .pipe(
            map((rawHistoricalData) =>
              rawHistoricalData.some((data) => (data ? true : false))
            )
          )
      : of(false);
  }

  private isRiskReturnProfileDataAvailable$(
    fundId: FundId
  ): Observable<boolean> {
    return this.siteConfig.isTraded(fundId)
      ? this.riskReturnProfileDataService
          .register(riskReturnProfileQuery, {
            fundId,
          })
          .pipe(
            map((riskReturnProfileData) => {
              return riskReturnProfileData?.points?.some((point) =>
                (point.data[0] as Array<number>)?.some((dataValue) => dataValue)
              );
            })
          )
      : of(false);
  }

  private isViivDataAvailable$(
    fundId: FundId,
    shareClassCode: ShareClassCode
  ): Observable<boolean> {
    return this.dataService
      .register(productDetailsPricingQuery, {
        fundid: fundId,
        shareclasscode: shareClassCode,
        countrycode: this.siteConfig.getPdsCountry(),
        languagecode: this.siteConfig.getPdsLanguage(),
      })
      .pipe(
        map((rawHistoricalData) =>
          hasValues(
            rawHistoricalData?.data?.PricesHistory?.prices as any[],
            'viiv'
          )
        )
      );
  }

  /**
   * Set the observable value true/false for non logged in VIP pages
   * @param status boolean value
   */
  setVIPNonLoggedInStatus(status: boolean) {
    this.isVIPNonLoggedIn$.next(status);
  }
}
